
import {
  defineComponent, PropType, computed, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  AttributionTouch,
  getSaleAttributionDetail,
  SaleAttributionDetail,
} from '@/composables/stats/saleAttribution';

// Aligné sur libs/common/src/spmWorkflows/enums.ts → ChannelIntegerEnum.
const CHANNEL_LABELS: Record<number, string> = {
  1: 'Email',
  2: 'SMS',
  3: 'Popup',
  4: 'Smart Content',
  5: 'FB Messenger',
  6: 'Push',
  7: 'WhatsApp',
  8: 'RCS',
  9: 'Teaser',
};

const compactToSeconds = (compact: string | null): number | null => {
  if (!compact || compact === '0') return null;
  const padded = compact.padStart(14, '0');
  const y = Number(padded.slice(0, 4));
  const m = Number(padded.slice(4, 6)) - 1;
  const d = Number(padded.slice(6, 8));
  const h = Number(padded.slice(8, 10));
  const mi = Number(padded.slice(10, 12));
  const s = Number(padded.slice(12, 14));
  if (!y) return null;
  return Math.floor(Date.UTC(y, m, d, h, mi, s) / 1000);
};

const formatLagSeconds = (seconds: number): string => {
  const abs = Math.abs(seconds);
  if (abs < 60) return `${seconds} s`;
  if (abs < 3600) return `${Math.round(seconds / 60)} min`;
  if (abs < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} j`;
};

export default defineComponent({
  name: 'SaleAttributionModal',
  components: { Dialog, SpmButton },
  props: {
    modelValue: { type: Boolean, default: false },
    idShopOrder: { type: [String, Number] as PropType<string | number | null>, default: null },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const detail = ref<SaleAttributionDetail | null>(null);
    const loading = ref(false);

    // Fusionne touches propres + touches croisées de tous les groupes en une
    // liste unique, dédupliquée par (kind, idSend), triée globalement par
    // priorité d'attribution (winner DESC puis dernière interaction DESC).
    const allTouches = computed<AttributionTouch[]>(() => {
      if (!detail.value) return [];
      const merged = new Map<string, AttributionTouch>();
      for (const grp of detail.value.groups) {
        for (const touch of [...grp.touches, ...grp.crossChannelTouches]) {
          const key = `${touch.kind}:${touch.idSend}`;
          const existing = merged.get(key);
          if (!existing || (!existing.isWinner && touch.isWinner)) {
            merged.set(key, touch);
          }
        }
      }
      const lastInteractionTs = (touch: AttributionTouch): number => {
        const raw = touch.dateLastClick ?? touch.dateLastOpen ?? touch.dateSend;
        if (!raw) return 0;
        return Number(raw.padStart(14, '0'));
      };
      const sorted = Array.from(merged.values()).sort((a, b) => {
        if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
        return lastInteractionTs(b) - lastInteractionTs(a);
      });
      const ranked = sorted.map((touch, idx) => ({ ...touch, rank: idx + 1 }));
      const persistedWinnerIsTracked = ranked.some((tt) => tt.isWinner && tt.isTracked);
      if (!persistedWinnerIsTracked) {
        const effectiveWinner = ranked.find((tt) => tt.isTracked);
        if (effectiveWinner) {
          return ranked.map((tt) => ({
            ...tt,
            isEffectiveWinner: tt.idSend === effectiveWinner.idSend && tt.kind === effectiveWinner.kind,
          }));
        }
      }
      return ranked.map((tt) => ({ ...tt, isEffectiveWinner: tt.isWinner }));
    });

    const totalAmount = computed(() => {
      if (!detail.value) return 0;
      return detail.value.groups.reduce((sum, g) => sum + g.amount, 0);
    });

    const currency = computed(() => {
      if (!detail.value || detail.value.groups.length === 0) return 'EUR';
      return detail.value.groups[0].currency || 'EUR';
    });

    const primarySaleDate = computed<string | null>(() => {
      if (!detail.value || detail.value.groups.length === 0) return null;
      return detail.value.groups[0].dateCreation || null;
    });

    const formattedSaleDate = computed(() => {
      if (!primarySaleDate.value) return '—';
      const padded = primarySaleDate.value.padStart(14, '0');
      return `${padded.slice(6, 8)}/${padded.slice(4, 6)}/${padded.slice(0, 4)} ${padded.slice(8, 10)}:${padded.slice(10, 12)}`;
    });

    const trackedCount = computed(() => allTouches.value.filter((tt) => tt.isTracked).length);
    const untrackedCount = computed(() => allTouches.value.length - trackedCount.value);
    const autoTouchesCount = computed(() => allTouches.value.filter((tt) => tt.kind === 'automation').length);
    const bulkTouchesCount = computed(() => allTouches.value.filter((tt) => tt.kind === 'bulk').length);

    const winnerLagDisplay = computed<string | null>(() => {
      const winner = allTouches.value.find((tt) => tt.isEffectiveWinner);
      if (!winner) return null;
      const saleSec = compactToSeconds(primarySaleDate.value);
      const interSec = compactToSeconds(winner.dateLastClick ?? winner.dateLastOpen);
      if (saleSec === null || interSec === null) return null;
      return formatLagSeconds(saleSec - interSec);
    });

    const formatAmount = (amount: number, ccy: string): string => {
      try {
        return new Intl.NumberFormat(locale.value, {
          style: 'currency',
          currency: ccy || 'EUR',
        }).format(amount);
      } catch (e) {
        return `${amount.toFixed(2)} ${ccy}`;
      }
    };

    const formatCompactDate = (raw: string | null): string => {
      if (!raw || raw === '0') return '—';
      const padded = raw.padStart(14, '0');
      return `${padded.slice(6, 8)}/${padded.slice(4, 6)} ${padded.slice(8, 10)}:${padded.slice(10, 12)}`;
    };

    const channelLabel = (channel: number | null): string => {
      if (channel === null) return '—';
      return CHANNEL_LABELS[channel] ?? `#${channel}`;
    };

    const lagSinceInteractionLabel = (touch: AttributionTouch): string => {
      const saleSec = compactToSeconds(primarySaleDate.value);
      const interSec = compactToSeconds(touch.dateLastClick ?? touch.dateLastOpen);
      if (saleSec === null || interSec === null) return '—';
      return formatLagSeconds(saleSec - interSec);
    };

    const fetch = async () => {
      if (!props.idShopOrder) {
        detail.value = null;
        return;
      }
      loading.value = true;
      try {
        detail.value = await getSaleAttributionDetail(props.idShopOrder);
      } finally {
        loading.value = false;
      }
    };

    watch(() => [props.modelValue, props.idShopOrder], async ([visible]) => {
      if (visible) {
        await fetch();
      } else {
        detail.value = null;
      }
    }, { immediate: true });

    const handleClose = () => {
      emit('update:modelValue', false);
    };

    return {
      t,
      detail,
      loading,
      allTouches,
      totalAmount,
      currency,
      formattedSaleDate,
      trackedCount,
      untrackedCount,
      autoTouchesCount,
      bulkTouchesCount,
      winnerLagDisplay,
      formatAmount,
      formatCompactDate,
      channelLabel,
      lagSinceInteractionLabel,
      handleClose,
    };
  },
});
